import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import React from 'react'

import { SelectableCard } from 'features/charts-v2/components/BreakdownCard'
import { MetricSelectHeader } from 'features/charts-v2/components/MetricSelectHeader'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import { FilterTypes } from 'features/filters/types'

export interface MetricControlProps {
  selectedMetrics: MetricType[]
  setSelectedMetrics: (metrics: MetricType[]) => void
  // Constraint props
  isDisabled?: boolean
  disabledTooltip?: string
  metricOptions?: Record<MetricType, boolean>
  // Label configuration
  labels?: {
    primary?: string
    secondary?: string
    tertiary?: string
    quaternary?: string
  }
  // Card label mode
  cardLabelMode?: 'tooltip' | 'text'
  // Whether to show the small grey card label above the card content
  showCardLabels?: boolean
  // Optional cross-panel sync toggle, rendered in the header left of the add button.
  syncToggle?: React.ReactNode
}

export const MetricControl: React.FC<MetricControlProps> = ({
  selectedMetrics,
  setSelectedMetrics,
  isDisabled = false,
  disabledTooltip,
  metricOptions,
  labels,
  cardLabelMode,
  showCardLabels = false,
  syncToggle,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Each metric slot has a stable id that travels with the slot through
  // reorders, so dnd-kit's drop animation interpolates to the correct
  // post-reorder position. Value-based ids would collide on duplicates
  // (e.g. two Sentences cards); positional ids break the drop animation
  // because the id stays put while the value moves.
  const slotIdCounterRef = React.useRef(0)
  const makeSlotId = (): string => `metric-slot-${slotIdCounterRef.current++}`
  const [slotIds, setSlotIds] = React.useState<string[]>(() =>
    selectedMetrics.map(makeSlotId),
  )

  // Sync slot ids to the metric count during render so no render pass ever
  // sees a length mismatch (an effect-based sync leaves the newly added card
  // with an undefined key/id for one render).
  if (slotIds.length !== selectedMetrics.length) {
    setSlotIds(
      selectedMetrics.length > slotIds.length
        ? [
            ...slotIds,
            ...Array.from(
              { length: selectedMetrics.length - slotIds.length },
              makeSlotId,
            ),
          ]
        : slotIds.slice(0, selectedMetrics.length),
    )
  }

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = slotIds.indexOf(String(active.id))
      const newIndex = slotIds.indexOf(String(over.id))

      if (oldIndex !== -1 && newIndex !== -1) {
        setSelectedMetrics(arrayMove(selectedMetrics, oldIndex, newIndex))
        setSlotIds(arrayMove(slotIds, oldIndex, newIndex))
      }
    }
  }
  // Define all metric options with proper typing
  const allMetricOptions: Array<{
    field: MetricType
    alias: string
    filter_type: FilterTypes.SELECT
    values: []
    enabled?: boolean
  }> = [
    {
      field: METRICS.RECORD_COUNT.id,
      alias: 'Records',
      filter_type: FilterTypes.SELECT,
      values: [],
      enabled: metricOptions?.[METRICS.RECORD_COUNT.id] ?? true,
    },
    {
      field: METRICS.SENTENCE_COUNT.id,
      alias: 'Sentences',
      filter_type: FilterTypes.SELECT,
      values: [],
      enabled: metricOptions?.[METRICS.SENTENCE_COUNT.id] ?? true,
    },
    {
      field: METRICS.RATING.id,
      alias: 'Rating',
      filter_type: FilterTypes.SELECT,
      values: [],
      enabled: metricOptions?.[METRICS.RATING.id] ?? true,
    },
    {
      field: METRICS.SENTIMENT.id,
      alias: 'Sentiment',
      filter_type: FilterTypes.SELECT,
      values: [],
      enabled: metricOptions?.[METRICS.SENTIMENT.id] ?? true,
    },
    // {
    //   field: METRICS.NET_PROMOTER_SCORE.id,
    //   alias: 'Net Promoter Score',
    //   filter_type: FilterTypes.SELECT,
    //   values: [],
    //   enabled: metricOptions?.[METRICS.NET_PROMOTER_SCORE.id] ?? true,
    // },
  ]

  // Handle metric selection change
  const handleMetricChange = (metricFields: MetricType[]): void => {
    // Support multiple metric selection
    setSelectedMetrics(metricFields)
  }

  const handleMetricEdit = (index: number, newMetric: MetricType): void => {
    setSelectedMetrics(
      selectedMetrics.map((metric, i) => (i === index ? newMetric : metric)),
    )
  }

  const handleMetricRemove = (index: number): void => {
    setSelectedMetrics(selectedMetrics.filter((_, i) => i !== index))
    setSlotIds(slotIds.filter((_, i) => i !== index))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <MetricSelectHeader
        options={allMetricOptions}
        onSelect={handleMetricChange}
        selectedMetrics={selectedMetrics}
        isDisabled={isDisabled}
        disabledTooltip={disabledTooltip}
        syncToggle={syncToggle}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* Render draggable metric cards */}
        {selectedMetrics.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={slotIds}
              strategy={verticalListSortingStrategy}
            >
              {selectedMetrics.map((metric, index) => {
                const label =
                  index === 0
                    ? labels?.primary
                    : index === 1
                      ? labels?.secondary
                      : index === 2
                        ? labels?.tertiary
                        : index === 3
                          ? labels?.quaternary
                          : undefined
                return (
                  <SelectableCard
                    key={slotIds[index]!}
                    mode="metric"
                    id={slotIds[index]!}
                    selectedMetric={metric}
                    metricOptions={allMetricOptions}
                    onEdit={(newMetric) => handleMetricEdit(index, newMetric)}
                    onRemove={
                      selectedMetrics.length > 1
                        ? () => handleMetricRemove(index)
                        : undefined
                    }
                    enableDragDrop={true}
                    allSelectedMetrics={selectedMetrics}
                    label={label || undefined}
                    cardLabelMode={cardLabelMode}
                    showCardLabels={showCardLabels}
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  )
}
